import React, {FC, ReactNode, useMemo} from "react"
import {NavLink, useNavigate, useParams} from "react-router-dom"
import {useApiCall, useApiEndpoint} from "@jane/lib/src/api"
import {config} from "../../config";
import { ContentContainer } from "@jane/lib/src/components/content/ContentContainer";
import { Loading } from "@jane/lib/src/components/Loading";
import { PageHeader } from "@jane/lib/src/components/content/PageHeader";
import moment from "moment";
import {Button} from "../../../../lib/src/components/form/Button";
import {faPaperclip, faPaperPlane} from "@fortawesome/pro-regular-svg-icons";
import {Card} from "../../../../lib/src/components/Card";
import {faPencil, faTrash, faUpload} from "@fortawesome/free-solid-svg-icons";
import {faUndo} from "@fortawesome/pro-light-svg-icons";
import {DeleteBroadcastModal} from "../../modals/DeleteBroadcastModal";
import {EditBroadcastContentModal} from "../../modals/EditBroadcastContentModal";
import {UnpublishBroadcastModal} from "../../modals/UnpublishBroadcastModal";
import {PublishBroadcastModal} from "../../modals/PublishBroadcastModal";
import {EditBroadcastTargetModal} from "../../modals/EditBroadcastTargetModal";
import {useTeamData} from "../../resources/TeamContext";
import {EditBroadcastAttachmentModal} from "../../modals/EditBroadcastAttachmentModal";
import {EditBroadcastCoverImageModal} from "../../modals/EditBroadcastCoverImageModal";
import {useModal} from "@jane/lib/src/components/layout/ModalProvider";
import {limitFilterOptions} from "../../../../lib/src/api/repositories/broadcast-repository"

export default function BroadcastsDetailsPage(): ReactNode {
  const {broadcastId} = useParams()
  const id = broadcastId ? parseInt(broadcastId) : null
  if (id) {
    return <BroadcastsDetailsPageContent broadcastId={id} />
  }
  return <div>...</div>
}
const BroadcastsDetailsPageContent: FC<{broadcastId: number}> = props => {
  const {broadcasts: broadcastsApi, teams: teamsApi} = useApiCall(config)
  const {resource: broadcast, error, reload} = useApiEndpoint(() => broadcastsApi.read(props.broadcastId))
  const {teams} = useTeamData()

  const navigate = useNavigate()

  const targetTeam = useMemo(() => {
    return teams?.find(t => t.id === broadcast?.target_team_id) ?? null
  }, [broadcast, teams])

  const deleteBroadcastModal = useModal({title: "Publicatie verwijderen", body: <DeleteBroadcastModal broadcast={broadcast!} onFinished={() => navigate('/broadcasts')} />})
  const unpublishBroadcastModal = useModal({title: "Publicatie intrekken", body: <UnpublishBroadcastModal broadcast={broadcast!} onFinished={() => reload()} />})
  const publishBroadcastModal = useModal({title: "Publicatie versturen", body: <PublishBroadcastModal broadcast={broadcast!} onFinished={() => reload()} />})
  const editBroadcastModal = useModal({title: "Publicatie bewerken", body: <EditBroadcastContentModal broadcast={broadcast!} onFinished={() => reload()} />})
  const editBroadcastTargetModal = useModal({title: "Publicatie bewerken", body: <EditBroadcastTargetModal teams={teams} broadcast={broadcast!} onFinished={() => reload()} />})
  const editBroadcastAttachmentModal = useModal({title: "Publicatie bijlage bewerken", body: <EditBroadcastAttachmentModal broadcast={broadcast!} onFinished={() => reload()} />})
  const editBroadcastCoverImageModal = useModal({title: "Publicatie foto bewerken", body: <EditBroadcastCoverImageModal broadcast={broadcast!} onFinished={() => reload()} />})

  return <ContentContainer>
    <NavLink to={'/broadcasts'} className={'text-brand-primary underline'}>Terug naar overzicht</NavLink>

    <Loading loading={broadcast === null}>
      {broadcast && <>
        <div className={'flex flex-col xl:flex-row'}>
          <article className={'flex-1'}>
            {broadcast.cover_image_url ? <div className={'relative'}>
              <img src={broadcast.cover_image_url} alt="" className={'object-cover h-64 w-full rounded-lg mt-8 mb-8'} />
              <div className={'opacity-0 hover:opacity-100 absolute inset-0 bg-[#fff8] rounded-lg flex items-center justify-center'}>
                <Button type={'primary'} size={'sm'} icon={faUpload} text={'Afbeelding vervangen'} onClick={() => {
                  editBroadcastCoverImageModal.open()
                }}/>
              </div>
            </div> : <>
              <Button type={'secondary'} size={'sm'} icon={faUpload} text={'Afbeelding toevoegen'} onClick={() => {
                editBroadcastCoverImageModal.open()
              }}/>
            </>}
            <PageHeader>{broadcast.subject}</PageHeader>
            <div className={'text-brand-secondary text-xs -mt-6 mb-8'}>{moment(broadcast.updated_at).format('DD-MM-YYYY HH:mm')}</div>

            <div className={'mb-8'}>{broadcast.message}</div>

            <Button type={'primary'} size={'sm'} icon={faPencil} text={'Tekst Bewerken'} onClick={() => {
              editBroadcastModal.open()
            }} />
          </article>

          <aside className={'pt-8 xl:ml-6 min-w-96 flex-col space-y-4'}>
            <Card title={`Status: ${broadcast.status === 'draft' ? 'Concept' : 'Openbaar'}`}>
              <div className={'flex flex-col items-stretch space-y-4'}>
                <table className={'w-full'}>
                  <tbody>
                    <tr><td>Ontvangers</td><td>{targetTeam?.name}</td></tr>
                    <tr><td></td><td>{broadcast.target_user_subs.length} {broadcast.target_type === 'clients' ? 'cliënten' : 'medewerkers'}</td></tr>
                    <tr><td>Gelimiteerd tot:</td><td>{limitFilterOptions[broadcast.limit_filter]}</td></tr>
                    <tr><td>Verstuurd</td><td>{broadcast.published_user_subs.length}</td></tr>
                  </tbody>
                </table>
                <Button type={'secondary'} size={'sm'} icon={faPencil} text={'Ontvangers aanpassen'} onClick={() => {
                  editBroadcastTargetModal.open()
                }}/>
                {broadcast.status === 'published' && <Button type={'secondary'} size={'sm'} icon={faUndo} text={'Intrekken'} onClick={() => {
                  unpublishBroadcastModal.open()
                }}/>}
                {broadcast.status === 'draft' && <Button type={'primary'} size={'sm'} icon={faPaperPlane} text={'Publiceren'} onClick={() => {
                  publishBroadcastModal.open()
                }}/>}
                <Button type={'danger'} size={'sm'} icon={faTrash} text={'Verwijderen'} onClick={() => {
                  deleteBroadcastModal.open()
                }}/>
              </div>
            </Card>

            <Card title={`Bijlage`}>
              <div className={'flex flex-col items-stretch space-y-4'}>
                {broadcast.attachment_url ? <>
                  <Button type={'primary'} size={'sm'} icon={faPaperclip} text={`${broadcast.attachment_name}`} onClick={() => {
                    window.open(broadcast.attachment_url!, '_blank')
                  }} />
                  <Button type={'secondary'} size={'sm'} icon={faUpload} text={'Bijlage vervangen'} onClick={() => {
                    editBroadcastAttachmentModal.open()
                  }}/>
                </> : <>
                  <Button type={'secondary'} size={'sm'} icon={faUpload} text={'Bijlage toevoegen'} onClick={() => {
                    editBroadcastAttachmentModal.open()
                  }}/>
                </>}
              </div>
            </Card>

          </aside>
        </div>

      </>}
    </Loading>
  </ContentContainer>
}

// function BroadcastOverview(props: { broadcast: Broadcast }) {
//   const {token} = useAuth()
//   return <div className={"bg-gray-50 rounded-lg p-6 my-3 flex flex-col space-y-3§"}>
//     <span className={"text-gray-600 italic mb-3"}>Verzonden op {new Date(props.broadcast.updated_at).toLocaleDateString('nl')}</span>
//     <PageHeader>{props.broadcast.subject}</PageHeader>
//     <p>{props.broadcast.message}</p>
//     {props.broadcast.attachment_name !== null && <div className={"flex px-4 py-3 mt-3 border border-gray-100 rounded-sm items-center"}>
//       <FontAwesomeIcon icon={['fas', 'paperclip']} className={"h-4 w-4 mr-3 font-light text-gray-600"} />
//       <div className={"flex-1"}>
//         { props.broadcast.attachment_name }
//       </div>
//       <a href={`${props.broadcast.attachment_path}&accessToken=${token}`} target={'_blank'} className={"flex items-center"} rel="noreferrer">Download<FontAwesomeIcon className={"h-4 w-4 ml-2"} icon={['fas', 'external-link-alt']} /></a>
//     </div> }
//   </div>
// }
